import { useState } from 'react';

import type { ClickStatus } from '@coursera/event-pulse-types';

import * as actions from 'bundles/authentication-common/components/actions';
import {
  ARKOSE_SIGNUP_KEY,
  RECAPTCHA_ACTIONS,
  SIGNUP_REDESIGN_RECAPTCHA_ID,
} from 'bundles/authentication-common/constants/botManager';
import {
  emailRequirementsError,
  existingEmailError,
  generalErrorMessage,
  nameRequirementsError,
  passwordRequirementsErrors,
  responseMessageFactory,
} from 'bundles/authentication-common/constants/errors';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';
import type { ErrorResponse, SuccessResponse } from 'bundles/authentication-common/types';
import type { ArkoseBotManagerContext } from 'bundles/authentication-common/types/BotManagerTypes';
import { type EventData, trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

export type SignupErrorField = 'name' | 'email' | 'password';

export type UseEmailPasswordSignupOptions = {
  arkoseBotManagerContext?: ArkoseBotManagerContext;
  eventData: EventData;
  verificationEmailRedirectUrl?: string;
  navigateToLogin: () => void;
  onSuccess: (response: SuccessResponse) => void;
  onErrorField?: (field: SignupErrorField) => void;
};

export type UseEmailPasswordSignup = {
  signup: (values: Record<string, string>) => Promise<void | boolean | undefined>;
  message: string | JSX.Element;
  isRunning: boolean;
  errorField: SignupErrorField | undefined;
  setMessage: (message: string | JSX.Element) => void;
  setIsRunning: (isRunning: boolean) => void;
  trackSignupClick: (status: ClickStatus) => void;
  handleSuccess: (response: SuccessResponse) => void;
  handleError: (response: ErrorResponse) => void;
};

/**
 * Email-and-password signup submission: configures the bot manager for the signup form,
 * tracks click/attempt events, maps API error codes to messages/fields and exposes the
 * shared message/running state so callers (e.g. the LTI signup branch) can reuse it.
 */
const useEmailPasswordSignup = ({
  arkoseBotManagerContext,
  eventData,
  verificationEmailRedirectUrl,
  navigateToLogin,
  onSuccess,
  onErrorField,
}: UseEmailPasswordSignupOptions): UseEmailPasswordSignup => {
  const [message, setMessage] = useState<string | JSX.Element>('');
  const [isRunning, setIsRunning] = useState(false);
  const [errorField, setErrorField] = useState<SignupErrorField | undefined>();
  const { trackButton } = useTrackClickButton();

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      context: arkoseBotManagerContext,
      publicKey: ARKOSE_SIGNUP_KEY,
    },
    reCAPTCHA: {
      container: SIGNUP_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.signup },
    },
  });

  /* tracks click events to submit button regardless of successful or not */
  const trackSignupClick = (status: ClickStatus) => {
    trackButton({
      name: 'signup_with_email',
      clickStatus: status,
    });
  };

  /**
   * Handles errors that occur during the signup process by resetting the reCAPTCHA,
   * setting an appropriate error message, updating the UI, and reporting the input
   * field associated with the error.
   */
  const handleError = (response: ErrorResponse) => {
    trackSignupClick('invalid');

    resetBotManager();

    const { errorCode = '' } = response?.responseJSON || {};

    const messages = responseMessageFactory();

    if (messages[errorCode]) {
      if (errorCode === existingEmailError) {
        const errMessageForExistingAcc = messages[errorCode](navigateToLogin);
        setMessage(errMessageForExistingAcc);
        setIsRunning(false);
      } else {
        const msg = messages[errorCode as Exclude<keyof typeof messages, 'existingCourseraAccount'>];
        setMessage(msg);
        setIsRunning(false);
      }

      let field: SignupErrorField | undefined;
      if (errorCode === nameRequirementsError) {
        field = 'name';
      } else if (errorCode === existingEmailError || errorCode === emailRequirementsError) {
        field = 'email';
      } else if (passwordRequirementsErrors.includes(errorCode)) {
        field = 'password';
      }
      setErrorField(field);
      if (field) {
        onErrorField?.(field);
      }
    } else {
      setErrorField(undefined);
      setMessage(generalErrorMessage());
      setIsRunning(false);
    }
  };

  /**
   * Handles a successful signup by tracking the click, setting a success message and
   * calling the onSuccess callback.
   */
  const handleSuccess = (response: SuccessResponse) => {
    trackSignupClick('valid');
    setErrorField(undefined);
    const successMsg = responseMessageFactory().success;

    setMessage(successMsg);

    return onSuccess(response);
  };

  /**
   * Handles the user signup process by checking the reCAPTCHA token and calling the
   * signup action. If the signup is successful, it handles the success response;
   * otherwise, it handles the error response.
   */
  const signup = async (values: Record<string, string>): Promise<void | boolean | undefined> => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;
    const { email: signupEmail, name, password } = values;

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      const msg = generalErrorMessage();
      setErrorField(undefined);
      return setMessage(msg);
    }

    setIsRunning(true);

    trackAuthAttempts(eventData, 'signup');
    try {
      const response = await actions.signup({
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        email: signupEmail,
        name,
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        password,
        verificationEmailRedirectUrl,
        ...tokenData,
      });
      handleSuccess(response as SuccessResponse);
    } catch (error) {
      handleError(error as ErrorResponse);
    }

    return undefined;
  };

  return {
    signup,
    message,
    isRunning,
    errorField,
    setMessage,
    setIsRunning,
    trackSignupClick,
    handleSuccess,
    handleError,
  };
};

export default useEmailPasswordSignup;
